import type React from "react";
import { useState } from "react";
import { Answer } from "src/components/Exam/Answers/Answer/Answer";
import { getAnswerTypesByExamType } from "src/components/Exam/Answers/Answer/getAnswerTypesByExamType";
import { useGetQuestionStats } from "src/hooks/useExam";
import { useKeyPress } from "src/hooks/useKeyPress";

interface Question {
	id: string | number;
	answers: Record<string, string>;
	correctAnswer: string;
	answer: string | null;
	isCanceled: boolean;
}

interface SetAnswerPayload {
	questionId: string | number;
	answer: string;
	isCorrectAnswer: boolean;
	isAnnulledQuestion: boolean;
}

export interface AnswersProps {
	question: Question;
	setAnswer: (payload: SetAnswerPayload) => Promise<void>;
	examType: string;
	examStatus: string;
	isStatsVisible: boolean;
	allowedKeysPress: boolean;
	renderAdditionalAction: ((letter: string) => React.ReactNode) | null;
	isDisabled: ((letter: string) => boolean) | null;
}

export function Answers({
	question,
	setAnswer,
	examType,
	examStatus,
	isStatsVisible,
	allowedKeysPress,
	renderAdditionalAction,
	isDisabled,
}: AnswersProps) {
	const [isLoadingKey, setIsLoadingKey] = useState<string | null>(null);
	const types = getAnswerTypesByExamType({
		examType,
		examStatus,
		question,
	});
	const { data: statistics, isLoading: isStasLoading } = useGetQuestionStats(
		question.id,
		!!isStatsVisible,
	);

	useKeyPress(["a", "b", "c", "d", "e"], (event: KeyboardEvent) => {
		if (allowedKeysPress) {
			if (isDisabled && !isDisabled(event.key)) {
				return setUserAnswer(event.key);
			}
		}
	});

	const getStatistics = (i: number): number | null => {
		if (!isStatsVisible || !question.answer || isStasLoading) {
			return null;
		}

		const {
			answersAPercent,
			answersBPercent,
			answersCPercent,
			answersDPercent,
			answersEPercent,
		} = statistics;
		const stats = [
			answersAPercent,
			answersBPercent,
			answersCPercent,
			answersDPercent,
			answersEPercent,
		];

		return stats[i];
	};

	const setUserAnswer = async (answer: string) => {
		setIsLoadingKey(answer);
		const answerLetter = answer.toUpperCase();

		try {
			await setAnswer({
				questionId: question.id,
				answer: answerLetter,
				isCorrectAnswer: question.correctAnswer.startsWith(answerLetter),
				isAnnulledQuestion: question.isCanceled,
			});
		} catch {
		} finally {
			setIsLoadingKey(null);
		}
	};

	const renderAnswers = () => {
		const answers = Object.entries(question.answers).sort((a, b) => {
			if (a[0] < b[0]) {
				return -1;
			}
			if (a[0] > b[0]) {
				return 1;
			}

			return 0;
		});

		return answers.map(([key, answer], index) => {
			if (!answer) {
				return null;
			}

			return (
				<Answer
					key={`${key}-${index}`}
					answer={answer}
					letter={key}
					answerType={types[key]}
					setAnswer={() => setUserAnswer(key)}
					statistics={getStatistics(index)}
					isLoading={isLoadingKey === key}
					renderAdditionalAction={renderAdditionalAction}
					isDisabled={isDisabled}
					data-test-id={`answer-option-${index}`}
				/>
			);
		});
	};

	return (
		<div id="answers-container">
			<ul className="flex gap-8 flex-col p-0">{renderAnswers()}</ul>
		</div>
	);
}
